import timezones from "@/helpers/timezones";
import { getDateTokens } from "@/helpers/dateFormat";
import type { CustomerReviewRow } from "@/types/CustomerReview";

/** LSP-21829 — small display helpers shared by the Reviews page, its cards and the About tab's Customer Review section. */

/** A review date in the organization's short date format ("Oct 6 2026" / "6 Oct 2026"), or "-" when there is none. */
export function formatCustomerReviewDate(value: string | null | undefined): string {
	if (!value) return "-";
	return timezones.formatDate(value, getDateTokens(timezones.getDateFormat()).dateShortMonth) || "-";
}

/**
 * Where a Reviews page row opens: its session or project detail page. Null when the server decided the viewer
 * cannot open it, or the row has no target — such a row is shown but never navigates.
 */
export function customerReviewTargetPath(row: Pick<CustomerReviewRow, "canOpen" | "target">): string | null {
	if (!row.canOpen || !row.target?.id) return null;
	const id = encodeURIComponent(row.target.id);
	return row.target.type === "project" ? `/projects/${id}` : `/conversations/${id}`;
}

/** Trimmed contact value, or null when it is missing or blank. */
export function presentContactValue(value: string | null | undefined): string | null {
	const trimmed = typeof value === "string" ? value.trim() : "";
	return trimmed ? trimmed : null;
}

/**
 * A `mailto:` / `sms:` / `tel:` link for a stored contact value. The value is kept as typed except for the characters a
 * URL would read as structure: an address such as `pat#sales@example.com` (valid, and accepted by contact validation)
 * would otherwise send the mail to `pat`, with the rest dropped as a fragment.
 */
export function contactHref(scheme: "mailto" | "sms" | "tel", value: string): string {
	return `${scheme}:${value.replace(/[%#?&]/g, (character) => encodeURIComponent(character))}`;
}
